import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FaAlignLeft,
  FaCalendar,
  FaClipboard,
  FaComments,
  FaPaperclip,
  FaTag,
  FaUser,
} from 'react-icons/fa'
import {
  useArchiveCard,
  useCard,
  useDeleteCard,
  useUpdateCard,
} from '../../hooks/useCards'
import { formatDate as formatDateLocale } from '../../utils/formatDate'
import { Button, ConfirmDialog, Input, LoadingSpinner, Modal } from '../common'
import { AttachmentList } from './AttachmentList'
import CardChangeLogModal from './CardChangeLogModal'
import { CardComments } from './CardComments'
import { CardMembers } from './CardMembers'
import './CardModal.css'
import { RichTextEditor } from './RichTextEditor'
import { RichTextViewer } from './RichTextViewer'

// Lazy load heavy modal
const AttachLabelModal = lazy(() =>
  import('../label').then((module) => ({ default: module.AttachLabelModal }))
)

interface CardModalProps {
  cardId: number
  boardId: number
  onClose: () => void
}

export const CardModal: React.FC<CardModalProps> = React.memo(
  ({ cardId, boardId, onClose }) => {
    const { t } = useTranslation()
    const { data: card, isLoading } = useCard(cardId)
    const updateCard = useUpdateCard()
    const archiveCard = useArchiveCard()
    const deleteCard = useDeleteCard()

    const [isEditingTitle, setIsEditingTitle] = useState(false)
    const [editedTitle, setEditedTitle] = useState('')
    const [isEditingDescription, setIsEditingDescription] = useState(false)
    const [editedDescription, setEditedDescription] = useState('')
    const [dueDate, setDueDate] = useState('')
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [showLabelModal, setShowLabelModal] = useState(false)
    const [showChangeLogModal, setShowChangeLogModal] = useState(false)

    useEffect(() => {
      if (card) {
        setEditedTitle(card.title)
        setEditedDescription(card.description || '')
        const dateValue = card.dueDate
        if (dateValue !== undefined && dateValue !== null) {
          setDueDate(dateValue.split('T')[0] as string)
        } else {
          setDueDate('')
        }
      }
    }, [card])

    const handleUpdateTitle = useCallback(async () => {
      if (!card || !editedTitle.trim() || editedTitle === card.title) {
        setIsEditingTitle(false)
        if (card) setEditedTitle(card.title)
        return
      }

      try {
        await updateCard.mutateAsync({
          id: cardId,
          data: {
            title: editedTitle.trim(),
            description: card.description,
            dueDate: card.dueDate,
          },
        })
        setIsEditingTitle(false)
      } catch (error) {
        setEditedTitle(card.title)
      }
    }, [cardId, editedTitle, card, updateCard])

    const handleUpdateDescription = useCallback(async () => {
      if (!card || editedDescription === (card.description || '')) {
        setIsEditingDescription(false)
        return
      }

      try {
        await updateCard.mutateAsync({
          id: cardId,
          data: {
            title: card.title,
            description: editedDescription.trim() || undefined,
            dueDate: card.dueDate,
          },
        })
        setIsEditingDescription(false)
      } catch (error) {
        setEditedDescription(card.description || '')
      }
    }, [cardId, editedDescription, card, updateCard])

    const handleUpdateDueDate = useCallback(
      async (newDueDate: string) => {
        if (!card) return

        try {
          await updateCard.mutateAsync({
            id: cardId,
            data: {
              title: card.title,
              description: card.description,
              dueDate: newDueDate || undefined,
            },
          })
          setDueDate(newDueDate)
        } catch (error) {
          const dateValue = card.dueDate
          if (dateValue !== undefined && dateValue !== null) {
            setDueDate(dateValue.split('T')[0] as string)
          } else {
            setDueDate('')
          }
        }
      },
      [cardId, card, updateCard]
    )

    // const handleArchive = useCallback(async () => {
    //   if (!card) return

    //   try {
    //     await archiveCard.mutateAsync({ id: cardId, archived: !card.archived })
    //     onClose()
    //   } catch (error) {
    //     // Error handled by hook
    //   }
    // }, [cardId, card, archiveCard, onClose])

    const handleDelete = useCallback(async () => {
      try {
        await deleteCard.mutateAsync(cardId)
        setShowDeleteConfirm(false)
        onClose()
      } catch (error) {
        // Error handled by hook
      }
    }, [cardId, deleteCard, onClose])

    const formatDate = useCallback(
      (dateString?: string) => {
        if (!dateString) return t('card.noDueDate')
        return formatDateLocale(dateString, 'MMM d, yyyy')
      },
      [t]
    )

    if (isLoading || !card) {
      return (
        <Modal isOpen={true} onClose={onClose} title={t('common.loading')}>
          <div className="card-modal__loading">
            <LoadingSpinner />
          </div>
        </Modal>
      )
    }

    return (
      <>
        <Modal isOpen={true} onClose={onClose} title="" size="lg">
          <div className="card-modal__content">
            <button
              className="card-modal__close-button"
              onClick={onClose}
              style={{ position: 'absolute', top: 10, right: 10 }}
            >
              ✕
            </button>
            {/* Title Section */}
            <div className="card-modal__section">
              <div className="card-modal__icon">
                <FaClipboard className="card-modal__icon-svg" />
              </div>
              <div className="card-modal__section-content">
                {isEditingTitle ? (
                  <Input
                    type="text"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    onBlur={handleUpdateTitle}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleUpdateTitle()
                      if (e.key === 'Escape') {
                        setIsEditingTitle(false)
                        setEditedTitle(card.title)
                      }
                    }}
                    autoFocus
                    className="card-modal__title-input"
                  />
                ) : (
                  <h2
                    className="card-modal__title"
                    onClick={() => setIsEditingTitle(true)}
                  >
                    {card.title}
                  </h2>
                )}
                <p className="card-modal__subtitle">
                  {t('card.inColumn')}{' '}
                  <span className="card-modal__column-name">
                    {/* Column name */}
                  </span>
                </p>
              </div>
            </div>

            <div className="card-modal__divider"></div>

            {/* Main Content */}
            <div className="card-modal__main">
              {/* Left Column */}
              <div className="card-modal__left">
                {/* Members Section */}
                <div className="card-modal__section">
                  <div className="card-modal__icon">
                    <FaUser className="card-modal__icon-svg" />
                  </div>
                  <div className="card-modal__section-content">
                    <CardMembers
                      cardId={cardId}
                      boardId={boardId}
                      members={card.members || []}
                    />
                  </div>
                </div>

                <div className="card-modal__divider"></div>

                {/* Labels Section */}
                <div className="card-modal__section">
                  <div className="card-modal__icon">
                    <FaTag className="card-modal__icon-svg" />
                  </div>
                  <div className="card-modal__section-content">
                    <div className="card-modal__section-header">
                      <h3 className="card-modal__section-title">
                        {t('card.labels')}
                      </h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowLabelModal(true)}
                        className="card-modal__edit-button"
                      >
                        {card.labels && card.labels.length > 0
                          ? t('common.edit')
                          : t('common.add')}
                      </Button>
                    </div>
                    {card.labels && card.labels.length > 0 ? (
                      <div className="card-modal__labels">
                        {card.labels.map((label) => (
                          <span
                            key={label.id}
                            className="card-modal__label"
                            style={{ backgroundColor: label.color }}
                          >
                            {label.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="card-modal__empty">{t('card.noLabels')}</p>
                    )}
                  </div>
                </div>

                <div className="card-modal__divider"></div>

                {/* Due Date Section */}
                <div className="card-modal__section">
                  <div className="card-modal__icon">
                    <FaCalendar className="card-modal__icon-svg" />
                  </div>
                  <div className="card-modal__section-content">
                    <h3 className="card-modal__section-title">
                      {t('card.dueDate')}
                    </h3>
                    <input
                      type="date"
                      value={dueDate}
                      onChange={(e) => handleUpdateDueDate(e.target.value)}
                      className="card-modal__date-input"
                    />
                    {card.dueDate && (
                      <p className="card-modal__date-display">
                        {formatDate(card.dueDate)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="card-modal__divider"></div>

                {/* Description Section */}
                <div className="card-modal__section">
                  <div className="card-modal__icon">
                    <FaAlignLeft className="card-modal__icon-svg" />
                  </div>
                  <div className="card-modal__section-content">
                    <div className="card-modal__section-header">
                      <h3 className="card-modal__section-title">
                        {t('card.description')}
                      </h3>
                      {!isEditingDescription && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setIsEditingDescription(true)}
                          className="card-modal__edit-button"
                        >
                          {t('common.edit')}
                        </Button>
                      )}
                    </div>
                    {isEditingDescription ? (
                      <RichTextEditor
                        content={editedDescription}
                        onChange={setEditedDescription}
                        onSave={handleUpdateDescription}
                        onCancel={() => {
                          setIsEditingDescription(false)
                          setEditedDescription(card.description || '')
                        }}
                        placeholder={t('card.addDescription')}
                      />
                    ) : (
                      <div
                        className="card-modal__description"
                        onClick={() => setIsEditingDescription(true)}
                      >
                        {card.description ? (
                          <RichTextViewer content={card.description} />
                        ) : (
                          <p className="card-modal__description-placeholder">
                            {t('card.addDescription')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="card-modal__divider"></div>

                {/* Comments Section */}
                <div className="card-modal__section">
                  <div className="card-modal__icon">
                    <FaComments className="card-modal__icon-svg" />
                  </div>
                  <div className="card-modal__section-content">
                    <div className="card-modal__section-header">
                      <h3 className="card-modal__section-title">
                        {t('card.comments')}
                      </h3>
                    </div>
                    <CardComments cardId={cardId} />
                  </div>
                </div>

                <div className="card-modal__divider"></div>

                {/* Attachments Section */}
                <div className="card-modal__section">
                  <div className="card-modal__icon">
                    <FaPaperclip className="card-modal__icon-svg" />
                  </div>
                  <div className="card-modal__section-content">
                    <div className="card-modal__section-header">
                      <h3 className="card-modal__section-title">
                        {t('card.attachments')}
                      </h3>
                    </div>
                    <AttachmentList cardId={cardId} />
                  </div>
                </div>
              </div>

              {/* Right Column - Actions */}
              <div className="card-modal__right">
                <div className="card-modal__actions">
                  <h3 className="card-modal__actions-title">
                    {t('common.actions')}
                  </h3>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowChangeLogModal(true)}
                    className="card-modal__action-button"
                  >
                    {t('card.activityLog', 'Faoliyat tarixi')}
                  </Button>
                  {/* <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleArchive}
                    className="card-modal__action-button"
                  >
                    <FaArchive className="card-modal__action-icon" />
                    {card.archived ? t('card.unarchive') : t('card.archive')}
                  </Button> */}
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="card-modal__action-button"
                  >
                    {t('common.delete')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Modal>

        <ConfirmDialog
          isOpen={showDeleteConfirm}
          title={t('card.deleteCard')}
          message={t('card.deleteCardConfirm', { title: card.title })}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          confirmText={t('common.delete')}
          confirmVariant="danger"
        />

        {showLabelModal && (
          <Suspense fallback={<LoadingSpinner />}>
            <AttachLabelModal
              isOpen={showLabelModal}
              onClose={() => setShowLabelModal(false)}
              cardId={cardId}
              boardId={boardId}
              attachedLabels={card.labels || []}
            />
          </Suspense>
        )}

        {showChangeLogModal && (
          <CardChangeLogModal
            cardId={cardId}
            cardTitle={card.title}
            onClose={() => setShowChangeLogModal(false)}
          />
        )}
      </>
    )
  }
)
